import { useMemo } from "react";
import useAssignment from "./useAssignment";
import useIncident from "./useIncident";
import { useStoreWrapper } from "../store/store";
import { careFields } from "../utils/helpers/constants";

const useSelectOptions = (facilityId: string) => {
  const { user } = useStoreWrapper((state) => ({ user: state.user }));
  const { getSystemFacilities } = useAssignment({
    profileId: "",
    userId: user?.userId,
  });

  const {
    getCaregiversInFacility,
    getResidentsInFacility,
    getProvidersInFacility,
    getStaffInFacility,
    getPickListOptions,
  } = useIncident({
    facilityId: facilityId,
  });

  const {
    isLoading: facilitiesLoading,
    data: facilitiesData,
    isError: facilitiesError,
  } = getSystemFacilities;

  const {
    isLoading: caregiversInFacilityLoading,
    data: caregiversInFacilityData,
    isError: caregiversInFacilityError,
  } = getCaregiversInFacility;

  const {
    isLoading: residentsInFacilityLoading,
    data: residentsInFacilityData,
    isError: residentsInFacilityError,
  } = getResidentsInFacility;

  const {
    isLoading: providersInFacilityLoading,
    data: providersInFacilityData,
    isError: providersInFacilityError,
  } = getProvidersInFacility;

  const {
    isLoading: staffInFacilityLoading,
    data: staffInFacilityData,
    isError: staffInFacilityError,
  } = getStaffInFacility;

  const facilities =
    facilitiesLoading || facilitiesError
      ? []
      : facilitiesData?.data?.data?.facilities;

  const caregiversInFacility =
    caregiversInFacilityLoading || caregiversInFacilityError
      ? []
      : caregiversInFacilityData?.data.data;
  const residentsInFacility =
    residentsInFacilityLoading || residentsInFacilityError
      ? []
      : residentsInFacilityData?.data.data;

  const providersInFacility =
    providersInFacilityLoading || providersInFacilityError
      ? []
      : providersInFacilityData?.data.data;
  const staffInFacility =
    staffInFacilityLoading || staffInFacilityError
      ? []
      : staffInFacilityData?.data.data;

  // format all fetched data for rendering in react-select in the format {...obj, label, value}
  const formattedFacilities = useMemo(
    () =>
      facilities.map(
        (facility: { facilityName: string; facilityId: string }) => {
          return {
            ...facility,
            label: facility.facilityName,
            value: facility.facilityId,
          };
        }
      ),
    [facilities]
  );

  const formattedCaregiversInFacility = useMemo(
    () =>
      caregiversInFacility.map((caregiverInFacility: any) => {
        return {
          ...caregiverInFacility,
          label: `${caregiverInFacility.firstName} ${caregiverInFacility.lastName}`,
          value: caregiverInFacility.userId,
        };
      }),
    [caregiversInFacility]
  );

  const formattedResidentsInFacility = useMemo(
    () =>
      residentsInFacility.map((residentInFacility: any) => {
        return {
          ...residentInFacility,
          label: `${residentInFacility.firstName} ${residentInFacility.lastName}`,
          value: residentInFacility.userId,
        };
      }),
    [residentsInFacility]
  );

  function getContactOnFacilityId(
    facilities: any[],
    facilityId: string
  ): string {
    const facility = facilities.find(
      (facility) => facility.facilityId === facilityId
    );
    return facility?.contactOnFacilityId || "";
  }

  const formattedStaffInFacility = useMemo(
    () =>
      staffInFacility.map((staffMemberInFacility: any) => {
        return {
          ...staffMemberInFacility,
          label: `${staffMemberInFacility.firstName} ${staffMemberInFacility.lastName}`,
          value: getContactOnFacilityId(
            staffMemberInFacility.facilities,
            facilityId
          ),
        };
      }),
    [staffInFacility]
  );

  const formattedProvidersInFacility = useMemo(
    () =>
      providersInFacility.map((providerInFacility: any) => {
        return {
          ...providerInFacility,
          label: `${providerInFacility.firstName} ${providerInFacility.lastName}`,
          value: getContactOnFacilityId(
            providerInFacility.facilities,
            facilityId
          ),
        };
      }),
    [providersInFacility]
  );

  const {
    isLoading: pickListOptionsLoading,
    data: pickListOptionsData,
    isError: pickListOptionsError,
  } = getPickListOptions;

  const pickListOptions =
    pickListOptionsLoading || pickListOptionsError
      ? {}
      : pickListOptionsData?.data.data;

  const { incident, ...rest } = pickListOptions;

  const formattedPickListOptions = Object.keys(incident || {}).reduce(
    (acc: any, fieldName: any) => {
      acc[fieldName] = incident[fieldName];
      return acc;
    },
    {}
  );

  const formattedRLPicklistOptions = {
    injuries: rest.injury,
    interviews: rest.interview,
    medication: rest.medication,
    perpetrators: rest.perpetrator,
    skinIssues: rest.skinIssues,
    careProvided: rest.careProvided,
  };

  const allLookUpOptions = {
    facility: formattedFacilities,
    caregiversInFacility: formattedCaregiversInFacility,
    residentsInFacility: formattedResidentsInFacility,
    staffInFacility: formattedStaffInFacility,
    providersInFacility: formattedProvidersInFacility,
  };

  const errors = {
    facilitiesError,
    residentsInFacilityError,
    caregiversInFacilityError,
    staffInFacilityError,
    providersInFacilityError,
    pickListOptionsError,
  };

  const loaders = {
    facilitiesLoading,
    residentsInFacilityLoading,
    caregiversInFacilityLoading,
    staffInFacilityLoading,
    providersInFacilityLoading,
    pickListOptionsLoading,
  };

  const errorGettingOptions = Object.values(errors || {}).some((err) => err);

  const optionsLoading = Object.values(loaders || {}).some((loader) => loader);

  const inlinePicklistOptions = Object.keys(careFields).reduce(
    (acc, fieldName) => {
      const correspondingFieldName =
        careFields[fieldName as keyof typeof careFields];
      if (formattedPickListOptions[correspondingFieldName]) {
        acc[fieldName] = formattedPickListOptions[correspondingFieldName];
      }
      return acc;
    },
    {} as { [key: string]: string }
  );

  return {
    allLookUpOptions,
    allPickListOptions: {
      ...formattedPickListOptions,
      ...inlinePicklistOptions,
    },
    rlPickListOptions: formattedRLPicklistOptions,
    errors,
    loaders,
    errorGettingOptions,
    optionsLoading,
  };
};

export default useSelectOptions;
